import { LambdaTrackerItem, LambdaTrackerUI } from "models/lambda-tracker"

interface LambdaInventoryTableProps {
    items: LambdaTrackerItem[]
    days: number
    onOpen: (item: LambdaTrackerItem) => void
}

interface Column {
    header: string
    csv: (i: LambdaTrackerItem) => string | number | null
    cell?: (i: LambdaTrackerItem) => React.ReactNode
    className?: string
}

const seconds = (ms: number | null) => (ms === null ? null : Number((ms / 1000).toFixed(2)))

// One row per Lambda with every inventory figure (the old spreadsheet), plus CSV export.
const LambdaInventoryTable = ({ items, days, onOpen }: LambdaInventoryTableProps) => {
    const errorsLabel = days >= 7 ? "Errors (7d)" : `Errors (${days}d)`

    const columns: Column[] = [
        {
            header: "Lambda",
            csv: i => i.functionName ?? i.lambdaFunctionName ?? i.code,
            cell: i => <>
                <i className={`${LambdaTrackerUI.healthIcon(i.currentHealth)} health-${i.currentHealth ?? "none"} me-1`} title={LambdaTrackerUI.healthLabel(i.currentHealth)} aria-hidden />
                <button type="button" className="btn btn-link p-0 fw-semibold" onClick={() => onOpen(i)}>{i.displayName}</button>
                <small className="d-block text-muted">{i.functionName ?? i.lambdaFunctionName ?? i.code}</small>
            </>,
        },
        { header: "Schedule", csv: i => LambdaTrackerUI.schedules(i), className: "wrap" },
        { header: "Avg (s)", csv: i => seconds(i.stats.avgMs), className: "num" },
        { header: "Min (s)", csv: i => seconds(i.stats.bestMs), className: "num" },
        { header: "Max (s)", csv: i => seconds(i.stats.worstMs), className: "num" },
        {
            header: errorsLabel, csv: i => i.stats.errors7d, className: "num",
            cell: i => <span className={i.stats.errors7d > 0 ? "text-danger fw-semibold" : undefined}>{i.stats.errors7d}</span>,
        },
        { header: "Billed (ms)", csv: i => i.stats.avgBilledMs === null ? null : Math.round(i.stats.avgBilledMs), className: "num" },
        { header: "Rate per ms", csv: i => i.stats.ratePerMsUsd, cell: i => LambdaTrackerUI.rate(i.stats.ratePerMsUsd), className: "num" },
        { header: "Cost per run", csv: i => i.stats.avgCostPerRunUsd, cell: i => LambdaTrackerUI.cost(i.stats.avgCostPerRunUsd), className: "num" },
        { header: "Cost per month", csv: i => i.stats.monthlyCostByScheduleUsd, cell: i => LambdaTrackerUI.cost(i.stats.monthlyCostByScheduleUsd), className: "num" },
        { header: "Mem used", csv: i => i.stats.peakMemoryUsedMb, cell: i => LambdaTrackerUI.memory(i.stats.peakMemoryUsedMb), className: "num" },
        { header: "Mem set", csv: i => i.stats.memorySizeMb, cell: i => LambdaTrackerUI.memory(i.stats.memorySizeMb), className: "num" },
        { header: "Sync button", csv: i => i.syncButtonScreens ?? "No", cell: i => i.syncButtonScreens ?? <span className="text-muted">No</span> },
        { header: "Tables in RDS", csv: i => i.rdsTables, className: "wrap" },
        { header: "Acumatica GIs", csv: i => i.acumaticaGIs, className: "wrap" },
        {
            header: "Endpoint", csv: i => i.endpointUrl,
            cell: i => i.endpointUrl ? <a href={i.endpointUrl} target="_blank" rel="noreferrer" title={i.endpointUrl}>Open <i className="mdi mdi-open-in-new" /></a> : "-",
        },
        { header: "Notes", csv: i => i.notes, className: "wrap" },
    ]

    const exportCsv = () => {
        const escape = (v: string | number | null) => {
            const s = v === null || v === undefined ? "" : String(v)
            return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
        }
        const lines = [
            ["Name", ...columns.map(c => c.header)].map(escape).join(","),
            ...items.map(i => [i.displayName, ...columns.map(c => c.csv(i))].map(escape).join(",")),
        ]
        const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" })
        const url = URL.createObjectURL(blob)
        const a = document.createElement("a")
        a.href = url
        a.download = `lambda-inventory-${new Date().toISOString().slice(0, 10)}.csv`
        a.click()
        URL.revokeObjectURL(url)
    }

    return (
        <div className="lambda-inventory">
            <div className="d-flex justify-content-between align-items-center mb-2">
                <small className="text-muted">Times, errors, memory and cost come from the runs of the last {days} days in this environment. Cost per month uses the schedule.</small>
                <button type="button" className="secondary-button" onClick={exportCsv}>
                    Export CSV <i className="mdi mdi-download" />
                </button>
            </div>
            <div className="table-responsive">
                <table className="table table-sm table-hover align-middle mb-0">
                    <thead>
                        <tr>{columns.map(c => <th key={c.header} className={c.className}>{c.header}</th>)}</tr>
                    </thead>
                    <tbody>
                        {items.length === 0 && (
                            <tr><td colSpan={columns.length} className="text-center text-muted py-4">No Lambdas match these filters.</td></tr>
                        )}
                        {items.map(i => (
                            <tr key={i.id} className={i.isActive ? undefined : "inactive"}>
                                {columns.map(c => (
                                    <td key={c.header} className={c.className}>{c.cell ? c.cell(i) : (c.csv(i) ?? "-")}</td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    )
}

export default LambdaInventoryTable
